import React, { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { mergeLang } from "../../lib/i18n";
import { Switch } from "../../components/ui/switch";
import { Field, ImageField, ListEditor, inputCls } from "./fields";
import { STATES } from "../../lib/states";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";

const SECTIONS = [
  ["general", "Brand & Contact"],
  ["hero", "Hero"],
  ["slides", "Promo Slider"],
  ["deal", "Weekly Deal"],
  ["brands", "Brands"],
  ["testimonials", "Reviews"],
  ["about", "About Us"],
  ["faqs", "FAQs"],
];

const Card = ({ children }) => <div className="space-y-4 rounded-2xl border border-[#e3f0ed] bg-white p-6">{children}</div>;

const ContentTab = ({ onChange }) => {
  const [c, setC] = useState(null);
  const [sec, setSec] = useState("general");
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [lang, setLang] = useState("en");

  useEffect(() => { api.get("/content").then((r) => setC(r.data)); }, []);
  if (!c) return <Loader2 className="animate-spin text-[#0e8c7f]" />;

  const es = lang === "es";
  const v = es ? mergeLang(c, c.es || {}) : c;
  const set = (k, val) => setC(es ? { ...c, es: { ...(c.es || {}), [k]: val } } : { ...c, [k]: val });
  const setIn = (k, sub, val) => set(k, { ...v[k], [sub]: val });
  const txt = (k) => <input className={inputCls} value={v[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`content-${k}`} />;
  const txtIn = (k, sub, area) =>
    area ? <textarea className={`${inputCls} min-h-[90px]`} value={v[k]?.[sub] ?? ""} onChange={(e) => setIn(k, sub, e.target.value)} data-testid={`content-${k}-${sub}`} />
      : <input className={inputCls} value={v[k]?.[sub] ?? ""} onChange={(e) => setIn(k, sub, e.target.value)} data-testid={`content-${k}-${sub}`} />;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", c);
      setC(data);
      toast.success("Website content saved — live now");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const { data } = await api.post("/content/reset");
    setC(data);
    setConfirmReset(false);
    toast.success("Content reset to defaults");
    onChange?.();
  };

  return (
    <div data-testid="content-tab">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          <div className="flex shrink-0 rounded-full border border-[#dcebe8] bg-white p-1" data-testid="content-lang-toggle">
            {[["en", "English"], ["es", "Español"]].map(([k, l]) => (
              <button key={k} onClick={() => setLang(k)} className={`rounded-full px-3 py-1 text-[13px] font-bold transition-colors ${lang === k ? "bg-[#0e8c7f] text-white" : "text-[#1f3a36]"}`} data-testid={`content-lang-${k}`}>{l}</button>
            ))}
          </div>
          {SECTIONS.filter(([k]) => !es || k !== "brands").map(([k, l]) => (
            <button key={k} onClick={() => setSec(k)} className={`shrink-0 rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${sec === k ? "bg-[#0f2e2a] text-white" : "bg-white text-[#1f3a36] border border-[#dcebe8] hover:border-[#0e8c7f]"}`} data-testid={`content-section-${k}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setConfirmReset(true)} className="sw-btn-outline !py-2 !px-4 text-[14px]"><RotateCcw size={15} />Reset</button>
          <button onClick={save} disabled={saving} className="sw-btn-primary !py-2.5 disabled:opacity-60" data-testid="save-content-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save changes</button>
        </div>
      </div>

      {es && <p className="mb-4 rounded-xl bg-[#fff7e6] px-4 py-3 text-[13.5px] text-[#7a5300]" data-testid="content-es-note">Editing the Spanish (ES) version. Empty fields fall back to English. Click Save to publish.</p>}
      {sec === "general" && es && (
        <Card><Field label="Top bar text (Spanish)">{txt("topbar_text")}</Field></Card>
      )}
      {sec === "general" && !es && (
        <Card>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Business name">{txt("brand")}</Field>
            <Field label="Top bar text">{txt("topbar_text")}</Field>
            <Field label="Phone (display)">{txt("phone_display")}</Field>
            <Field label="Phone (dial format, e.g. +19314120007)">{txt("phone_raw")}</Field>
            <Field label="WhatsApp (display)">{txt("whatsapp_display")}</Field>
            <Field label="WhatsApp number (digits only, e.g. 17867581287)">{txt("whatsapp_raw")}</Field>
            <Field label="Email">{txt("email")}</Field>
            <Field label="Second email (shown next to the first everywhere)">{txt("email2")}</Field>
            <Field label="Address">{txt("address")}</Field>
            <Field label="Facebook URL">{txt("facebook")}</Field>
            <Field label="Holiday banner on home page"><select className={inputCls} value={c.holiday_banner || "auto"} onChange={(e) => set("holiday_banner", e.target.value)} data-testid="holiday-banner-mode"><option value="auto">Automatic (Dec 1 – Jan 2)</option><option value="on">Always on (until I turn it off)</option><option value="off">Off</option></select></Field>
            <Field label="Facebook visitor greeting (English)" full><textarea className={`${inputCls} min-h-[70px]`} value={c.source_greetings?.facebook?.en ?? ""} onChange={(e) => set("source_greetings", { ...(c.source_greetings || {}), facebook: { ...(c.source_greetings?.facebook || {}), en: e.target.value } })} data-testid="fb-greeting-en" /></Field>
            <Field label="Facebook visitor greeting (Spanish)" full><textarea className={`${inputCls} min-h-[70px]`} value={c.source_greetings?.facebook?.es ?? ""} onChange={(e) => set("source_greetings", { ...(c.source_greetings || {}), facebook: { ...(c.source_greetings?.facebook || {}), es: e.target.value } })} data-testid="fb-greeting-es" /></Field>
            <p className="text-[12.5px] text-[#6b7c79] sm:col-span-2">Shown to visitors who open <b>stripwisepays.com/chat?src=facebook</b>. Use {"{agent}"} for today's agent name and {"{nm}"} to insert the visitor's name.</p>
            <div className="md:col-span-2">
              <div className="mb-1.5 text-[13px] font-semibold text-[#3d504d]">Live chat agents: one is shown each day (rotates every 24 hours), always shown as online</div>
              <ListEditor items={v.chat_agents || []} onChange={(v) => set("chat_agents", v)} itemLabel="Agent" titleKey="name" newItem={{ name: "", photo: "" }}
                fields={[{ key: "name", label: "Display name", full: true }, { key: "speaks_spanish", label: "Speaks Spanish (shows \"Habla español\" badge)", type: "select", options: ["No", "Yes"] }, { key: "bio", label: "Short bio (English)", type: "textarea" }, { key: "bio_es", label: "Short bio (Spanish)", type: "textarea" }, { key: "photo", label: "Photo", type: "image" }]} />
            </div>
          </div>
        </Card>
      )}

      {sec === "hero" && (
        <Card>
          <Field label="Badge">{txtIn("hero", "badge")}</Field>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Headline">{txtIn("hero", "title")}</Field>
            <Field label="Highlighted words (teal)">{txtIn("hero", "highlight")}</Field>
          </div>
          <Field label="Subtitle">{txtIn("hero", "subtitle", true)}</Field>
          <Field label="Hero video URL (plays muted on loop; leave empty to show the image instead)">{txtIn("hero", "video")}</Field>
          <Field label="Hero image / video poster"><ImageField value={v.hero?.image} onChange={(v) => setIn("hero", "image", v)} /></Field>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Floating card title">{txtIn("hero", "payout_title")}</Field>
            <Field label="Floating card text">{txtIn("hero", "payout_text")}</Field>
          </div>
        </Card>
      )}

      {sec === "slides" && (
        <Card>
          <ListEditor items={v.slides} onChange={(v) => set("slides", v)} itemLabel="Slide" titleKey="title"
            newItem={{ eyebrow: "", title: "", text: "", image: "", color: "teal" }}
            fields={[{ key: "eyebrow", label: "Eyebrow" }, { key: "color", label: "Panel color", type: "select", options: ["teal", "orange", "dark"] }, { key: "title", label: "Title", full: true }, { key: "text", label: "Text", type: "textarea" }, { key: "image", label: "Image", type: "image" }]} />
        </Card>
      )}

      {sec === "deal" && (
        <Card>
          <label className="flex items-center gap-3"><Switch checked={!!v.deal?.enabled} onCheckedChange={(v) => setIn("deal", "enabled", v)} data-testid="deal-enabled-switch" /><span className="text-[14px] font-medium">Show weekly deal banner</span></label>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Label">{txtIn("deal", "label")}</Field>
            <Field label="Product title">{txtIn("deal", "title")}</Field>
            <Field label="Amount (bold)">{txtIn("deal", "amount")}</Field>
            <Field label="Trailing text">{txtIn("deal", "text")}</Field>
          </div>
          <Field label="Image"><ImageField value={v.deal?.image} onChange={(v) => setIn("deal", "image", v)} /></Field>
        </Card>
      )}

      {sec === "brands" && (
        <Card>
          <ListEditor items={v.brands} onChange={(v) => set("brands", v)} itemLabel="Brand" titleKey="name" newItem={{ name: "", image: "" }}
            fields={[{ key: "name", label: "Brand name", full: true }, { key: "image", label: "Image", type: "image" }]} />
        </Card>
      )}

      {sec === "testimonials" && (
        <Card>
          <label className="mb-4 flex items-center gap-2 text-[14px] font-semibold text-[#3d504d]"><input type="checkbox" checked={!!c.show_older_reviews} onChange={(e) => setC({ ...c, show_older_reviews: e.target.checked })} data-testid="show-older-reviews-toggle" />Show older (non-verified) reviews on the website</label>
          <ListEditor items={c.testimonials} onChange={(val) => setC({ ...c, testimonials: val })} itemLabel="Review" titleKey="name" newItem={{ name: "", location: "", text: "", image: "", rating: "5", state: "Any" }}
            fields={es ? [{ key: "text", label: "English review (original)", type: "textarea" }, { key: "text_es", label: "Spanish review text", type: "textarea" }] : [{ key: "name", label: "Name" }, { key: "location", label: "Location (e.g. Houston, TX)" }, { key: "rating", label: "Star rating", type: "select", options: ["5", "4", "3", "2", "1"] }, { key: "state", label: "Feature on state page", type: "select", options: ["Any", ...STATES.map((x) => x.name)] }, { key: "text", label: "Review text", type: "textarea" }, { key: "image", label: "Photo", type: "image" }]} />
        </Card>
      )}

      {sec === "about" && (
        <Card>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Title">{txtIn("about", "title")}</Field>
            <Field label="Highlight quote">{txtIn("about", "quote")}</Field>
          </div>
          <Field label="Subtitle">{txtIn("about", "subtitle", true)}</Field>
          <div>
            <div className="mb-2 text-[13px] font-semibold text-[#3d504d]">Stats</div>
            <ListEditor items={v.about?.stats} onChange={(v) => setIn("about", "stats", v)} itemLabel="Stat" titleKey="label" newItem={{ value: "", label: "" }}
              fields={[{ key: "value", label: "Value" }, { key: "label", label: "Label" }]} />
          </div>
          <div>
            <div className="mb-2 text-[13px] font-semibold text-[#3d504d]">Story paragraphs</div>
            <ListEditor items={(v.about?.paragraphs || []).map((t) => ({ t }))} onChange={(v) => setIn("about", "paragraphs", v.map((x) => x.t || ""))} itemLabel="Paragraph" newItem={{ t: "" }}
              fields={[{ key: "t", label: "Text", type: "textarea" }]} />
          </div>
        </Card>
      )}

      {sec === "faqs" && (
        <Card>
          <ListEditor items={v.faqs} onChange={(v) => set("faqs", v)} itemLabel="FAQ" titleKey="q" newItem={{ q: "", a: "" }}
            fields={[{ key: "q", label: "Question", full: true }, { key: "a", label: "Answer", type: "textarea" }]} />
        </Card>
      )}

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all website content?</AlertDialogTitle>
            <AlertDialogDescription>All text, slides, reviews and FAQs go back to the original defaults. Products are not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reset}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContentTab;
